import React, { useContext, useEffect, useState } from 'react';
import { Text, View, Pressable } from 'react-native';
import {
  useQuery,
  useLazyQuery,
  useMutation,
  useSubscription,
} from '@apollo/client';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

import AuthContext from 'app/src/contexts/AuthContext';
import * as colors from 'app/src/styles/colors';
import Modal from 'app/src/elements/Modal';
import Print from 'app/src/elements/Print';
import { numberWithDelimiter } from 'app/src/utils/stringHelpers';

import Notification from './show';
import {
  CURRENT_USER_NOTIFICATION_COUNTS,
  USER_NOTIFICATION_COUNTS_UPDATED,
  CURRENT_USER_NOTIFICATIONS,
  USER_NOTIFICATION_UPDATED,
  UPDATE_ALL_USER_NOTIFICATIONS,
} from './queries';

const Container = styled(View)`
  position: relative;
`;

const CountContainer = styled(View)`
  position: absolute;
  left: 30px;
  top: 10px;
`;

const UnviewedCount = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.elRed};
`;

const MarkAll = styled(View)`
  flex-direction: row;
  justify-content: flex-end;
  gap: 20px;
  margin-bottom: 20px;
`;

const PAGE_LIMIT = 100;

const Notifications = ({ user }) => {
  const [showModal, setShowModal] = useState(false);
  const { apolloClient: client } = useContext(AuthContext);
  const [page, setPage] = useState(1);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  const offset = Math.max(page - 1, 0) * PAGE_LIMIT;

  const variables = { sellerUserId: user.id };

  const { data } = useQuery(CURRENT_USER_NOTIFICATION_COUNTS, { variables });

  const [getNotifications, { data: notificationsData }] = useLazyQuery(
    CURRENT_USER_NOTIFICATIONS,
    {
      fetchPolicy: 'network-only',
      variables: { ...variables, limit: PAGE_LIMIT, offset }
    }
  );

  const [updateAllUserNotifications] = useMutation(
    UPDATE_ALL_USER_NOTIFICATIONS
  );

  const markAll = (field, confirmation) => {
    if (!confirmation || confirm(confirmation)) {
      updateAllUserNotifications({ variables: { [field]: true } });
    }
  };

  useEffect(() => {
    if (updateRequired && showModal) {
      getNotifications();
      setUpdateRequired(false);
    }
  }, [showModal, updateRequired]);

  useSubscription(USER_NOTIFICATION_COUNTS_UPDATED, {
    variables,
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      if (!data || !data.userNotificationCountsUpdated) return;

      const updates = data.userNotificationCountsUpdated;

      client.writeQuery({
        query: CURRENT_USER_NOTIFICATION_COUNTS,
        variables,
        data: { currentUserNotificationCounts: updates },
      });

      setUpdateRequired(true);
    }
  });

  useEffect(() => {
    if (notificationsData) {
      setVisibleNotifications(
        notificationsData?.currentUserNotifications.
        filter(notification => !notification.hiddenAt).
        toSorted((a, b) => b.notification.id - a.notification.id)
      );
    }
  }, [notificationsData]);

  const unviewedCount = data?.currentUserNotificationCounts.unviewed || 0;
  const visibleCount = data?.currentUserNotificationCounts.visible || 0;
  const pages = Math.ceil(visibleCount / PAGE_LIMIT);

  return (
    <>
      <Container>
        <FontAwesomeIcon
          icon={faBell}
          size='2xl'
          style={{color: unviewedCount ? colors.elRed : colors.iconGray}}
          onClick={() => { setShowModal(!showModal); setUpdateRequired(true); }}
        />
        {unviewedCount > 0 &&
          <CountContainer>
            <UnviewedCount>{unviewedCount}</UnviewedCount>
          </CountContainer>
        }
      </Container>
      {showModal && (
        <Modal
          showModal={showModal}
          setShowModal={setShowModal}
          modalType="Notifications"
        >
          <MarkAll>
            {page > 1 && (
              <Pressable onPress={() => setPage(page - 1)}>
                <Text>Previous</Text>
              </Pressable>
            )}

            {page < pages && (
              <Pressable onPress={() => setPage(page + 1)}>
                <Text>Next</Text>
              </Pressable>
            )}

            <Pressable onPress={() => markAll('viewed')}>
              <Text>Mark All Viewed</Text>
            </Pressable>

            <Pressable onPress={() => markAll('hidden', 'Really dismiss all?')}>
              <Text>Dismiss All</Text>
            </Pressable>

            <Print
              path={`reports/notifications`}
              filename={`elag-notifications`}
            />
          </MarkAll>

          {visibleNotifications.map(userNotification => (
            <Notification
              key={userNotification.id}
              userNotification={userNotification}
              setShowModal={setShowModal}
            />
          ))}
        </Modal>
      )}
    </>
  );
};

export default Notifications;
